import { useContext } from 'react';
import SectionContext from './SectionContext';
import SectionWithFirstSectionNameFormMarginCorrection from './SectionWithFirstSectionNameFormMarginCorrection';
import SectionHeader from './SectionHeader';
import SectionContentGroup from './SectionContentGroup';
import ContentGroupType from '@/lib/constants/challengeModel/ContentGroupType.enum';
import ModelFieldEditorList from './ModelFieldEditorList';
import AddModelField from './AddModelField';
import getHighestFieldPosition from './getHighestFieldPosition';
import { type EditorField, type EditorSection } from '@/lib/types';
import SectionContentWrapper from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemFields/Section/SectionContentWrapper';
import SelectedModelIdContext from '../../SelectedModelIdContext';
import ModelsContext from '../../ModelsContext';

type ModelSectionEditorProps = {
  section: EditorSection;
  previousSection: EditorSection | null;
  nextSection: EditorSection | null;
};

const ModelSectionEditor = ({
  section,
  previousSection,
  nextSection,
}: ModelSectionEditorProps) => {
  const [models] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);
  const allFields = models[selectedModelId].fields;

  const sectionContentGroups = section.fields
    .map(({ id }) => allFields[id])
    .filter(field => typeof field !== 'undefined')
    .reduce(
      (sectionContentGroupsSoFar, field) => {
        sectionContentGroupsSoFar[field.contentGroup].push(field);

        return sectionContentGroupsSoFar;
      },
      {
        [ContentGroupType.LEFT]: [] as EditorField[],
        [ContentGroupType.RIGHT]: [] as EditorField[],
        [ContentGroupType.BOTTOM]: [] as EditorField[],
      },
    );

  const highestFieldPositions = {
    [ContentGroupType.LEFT]: getHighestFieldPosition(
      sectionContentGroups[ContentGroupType.LEFT],
    ),
    [ContentGroupType.RIGHT]: getHighestFieldPosition(
      sectionContentGroups[ContentGroupType.RIGHT],
    ),
    [ContentGroupType.BOTTOM]: getHighestFieldPosition(
      sectionContentGroups[ContentGroupType.BOTTOM],
    ),
  };

  const lowestFieldPositionInBottomContent =
    sectionContentGroups[ContentGroupType.BOTTOM][0]?.position ?? 0;

  return (
    <SectionContext.Provider value={{ section, previousSection, nextSection }}>
      <SectionWithFirstSectionNameFormMarginCorrection>
        <SectionHeader />
        <SectionContentWrapper>
          <SectionContentGroup which={ContentGroupType.LEFT}>
            <ModelFieldEditorList
              fields={sectionContentGroups[ContentGroupType.LEFT]}
              highestFieldPositions={highestFieldPositions}
              lowestFieldPositionInSectionBottomContent={
                lowestFieldPositionInBottomContent
              }
            />
            <AddModelField
              highestFieldPosition={
                highestFieldPositions[ContentGroupType.LEFT]
              }
            />
          </SectionContentGroup>
          <SectionContentGroup which={ContentGroupType.RIGHT}>
            <ModelFieldEditorList
              fields={sectionContentGroups[ContentGroupType.RIGHT]}
              highestFieldPositions={highestFieldPositions}
              lowestFieldPositionInSectionBottomContent={
                lowestFieldPositionInBottomContent
              }
            />
            <AddModelField
              highestFieldPosition={
                highestFieldPositions[ContentGroupType.RIGHT]
              }
            />
          </SectionContentGroup>
        </SectionContentWrapper>
        <SectionContentGroup which={ContentGroupType.BOTTOM}>
          <ModelFieldEditorList
            fields={sectionContentGroups[ContentGroupType.BOTTOM]}
            highestFieldPositions={highestFieldPositions}
            lowestFieldPositionInSectionBottomContent={
              lowestFieldPositionInBottomContent
            }
          />
          <AddModelField
            highestFieldPosition={
              highestFieldPositions[ContentGroupType.BOTTOM]
            }
          />
        </SectionContentGroup>
      </SectionWithFirstSectionNameFormMarginCorrection>
    </SectionContext.Provider>
  );
};

export default ModelSectionEditor;
